/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
import { StructureEditorConfig } from '@Pages/StructureEditor/StructureEditorConstants';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import each from 'lodash/each';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isUndefined from 'lodash/isUndefined';
import union from 'lodash/union';
import flatten from 'lodash/flatten';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import map from 'lodash/map';
import extend from 'lodash/extend';
import indexOf from 'lodash/indexOf';
import isBoolean from 'lodash/isBoolean';
import isNaN from 'lodash/isNaN';

import MessageBroker from '@Services/MessageBroker';
import StructureEditorOptionsStorage from '@Services/AppDBStorage/Storages/StructureEditorOptionsStorage';

let defaultOptions = {
  context: null,
  groups: [],
};
let optionsPromise;

/** retrieves default options from cache if available or from BE */
export function fetchDefaultOptions(context = 'all') {
  return StructureEditorOptionsStorage.get(context)
    .then((data) => {
      if (data) {
        return data;
      }
      return MessageBroker.send(RequestUrls.structureOptions, { context }, { cache: false })
        .then((result) => {
          StructureEditorOptionsStorage.set(context, result);
          return result;
        })
        .catch(Logger.reject);
    })
    .catch(Logger.reject);
}

export function checkUserFilters(options, predefinedOptions) {
  each(predefinedOptions, (item) => {
    // setup predefined flags
    let filter = find(options, (option) => option && option.name === item.name && option.value === item.value);
    if (isUndefined(filter)) {
      const subOptions = flatten(map(options, 'subOptions'));
      filter = find(subOptions, (option) => option && option.name === item.name && option.value === item.value);
    } else {
      filter.predefined = true;
    }

    // setup input values
    filter = find(options, (option) => option.name === item.name && option.type === 'input');
    if (!isUndefined(filter)) {
      filter.value = item.value;
    }
  });
  each(options, (option) => {
    if (option.subOptions) {
      const predefinedSuboption = find(option.subOptions, { predefined: true });
      option.subsValue = predefinedSuboption && predefinedSuboption.value;
    }
  });
}

function getPredefinedOrFirstOption(flattenedOptions, options) {
  const predefinedOption = find(flattenedOptions, { predefined: true });
  return !isUndefined(predefinedOption) && predefinedOption.type === 'radio' ? predefinedOption.value : options[0].value;
}

function sanitizeInputOption(option) {
  if (option.type === 'input' && isNaN(parseInt(option.value, 10))) {
    option.value = undefined;
  }
}

function processGroup(group) {
  if (group.id === 'as' || !isArray(group.options)) return;

  const flattenedOptions = union(group.options, flatten(map(group.options, 'subOptions')));
  group.value = getPredefinedOrFirstOption(flattenedOptions, group.options);

  group.options.forEach(sanitizeInputOption);
  defaultOptions.groups.push(group);
}

export function initializeDefaultOptions() {
  return fetchDefaultOptions()
    .then((originalOptions) => {
      if (!originalOptions) return null;

      defaultOptions.context = originalOptions.context;
      checkUserFilters(flatten(map(originalOptions.groups, 'options')), originalOptions.options);

      originalOptions.groups.forEach(processGroup);

      return defaultOptions;
    })
    .catch(Logger.reject);
}

/** provides access to default structure editor options */
export function getDefaultOptions() {
  if (optionsPromise) {
    return optionsPromise.then((options) => cloneDeep(options)).catch(Logger.reject);
  }
  // initialize once
  optionsPromise = initializeDefaultOptions()
    .then((options) => cloneDeep(options))
    .catch(Logger.reject);
  return optionsPromise;
}

/** updates dependent option's state */
export function toggleAdditionalOptionsDisable(options, disabled) {
  each(options.groups, (value) => {
    if (
      StructureEditorConfig.optionTypes &&
      StructureEditorConfig.optionTypes.unshowed &&
      StructureEditorConfig.optionTypes.unshowed.indexOf(value.id) !== -1
    ) {
      // eslint-disable-next-line no-return-assign
      each(value.options, (value) => (value.disabled = disabled));
    }
  });
}
export function fixHopintoBatchSimilarity(options) {
  return map(options, (option) => {
    const ext =
      option.name === 'similarity'
        ? {
            name: 'searchBy',
            value: 'similarity',
          }
        : null;
    // Don't alter original option by reference - create new copy instead
    return extend({}, option, ext);
  });
}

function clearSubValues(option) {
  option.subsValue = null;
  if (option.value) {
    option.value = null;
    option.checked = false;
  }
}

function restoreSearchBy(option, optionValues) {
  const byOption = optionValues.find((opt) => opt.name === 'searchBy');
  if (byOption) {
    option.value = byOption.value;
  }
}

function restoreRadioSubValues(option, newOption) {
  if (option.subOptions && newOption && (newOption.value === 'sub_hetero' || newOption.value === 'substructure')) {
    option.subsValue = newOption.value;
  }
}

function restoreCheckRadio(option, newOption) {
  if (newOption) {
    option.checked = true;
    option.value = newOption.value;
    option.subsValue = newOption.value;
  }
}

/** resets cached options -  Shall we move this to profile service as it is used in profile preferences? */
export function resetOptions() {
  optionsPromise = null;
  defaultOptions = {
    groups: [],
    context: null,
  };
  StructureEditorOptionsStorage.clear();
}

/**
 * Updates an individual option based on provided values.
 */
function updateOption(option, optionValues, optionNames, similarMode) {
  const newOption = optionValues.find((newOpt) => option.name === newOpt.name);

  if (similarMode && option.subsValue) {
    clearSubValues(option);
  }

  if (option.id === 'by') {
    restoreSearchBy(option, optionValues);
  }

  switch (option.type) {
    case 'checkbox':
      option.checked = optionNames.has(option.name);
      break;
    case 'radio':
      restoreRadioSubValues(option, newOption);
      break;
    case 'input':
      option.value = get(newOption, 'value', null);
      break;
    case 'check_radio':
      restoreCheckRadio(option, newOption);
      break;
    default:
      break;
  }
}

/** preselects provided values in options and return them  */
export function restoreOptions(originalOptionValues) {
  const optionValues = fixHopintoBatchSimilarity(originalOptionValues);

  return getDefaultOptions()
    .then((defaultOptions) => {
      const options = cloneDeep(defaultOptions);
      const optionNames = new Set(optionValues.map((option) => option.name));
      const similarMode = optionValues.some((option) => option.value === 'similarity');

      if (optionNames.size === 0) return options;

      eachHierarchy(options.groups, (option) => updateOption(option, optionValues, optionNames, similarMode), null, {
        children: 'options',
      });

      toggleAdditionalOptionsDisable(options, similarMode);
      return options;
    })
    .catch(Logger.reject);
}

/** extracts necessary values from all options */
export function extractOptionValues(groups) {
  const options = [];
  each(groups, (value) => {
    if (value.options[0].type === 'radio') {
      options.push({
        name: value.options[0].name,
        value: value.value,
      });
    } else {
      each(value.options, (value) => {
        const isUncheckedCheckbox = indexOf(['checkbox', 'check_radio'], value.type) !== -1 && !value.checked;
        if (isUncheckedCheckbox || !value.value) {
          return;
        }
        options.push({
          name: value.name,
          value: value.value,
        });
      });
    }
  });
  return options;
}

/** forms options titles map for badges */
function getOptionTitles(groups) {
  const optionTitles = {};
  each(groups, (group) => {
    if (isArray(group.options)) {
      each(group.options, (option) => {
        // eslint-disable-next-line no-use-before-define
        addConformity(option);

        if (option.subOptions) {
          // eslint-disable-next-line no-use-before-define
          each(option.subOptions, (subOption) => addConformity(subOption));
        }
      });
    }
  });

  return optionTitles;

  function addConformity(option) {
    if (option.value && option.title) {
      optionTitles[option.value] = option.title;
    }
  }
}

/** calculates badges from provided options */
export function calculateBadges(options) {
  const badges = [];
  const optionTitles = getOptionTitles(options.groups);
  each(options.groups, (group) => {
    if (isArray(group.options)) {
      if (group.options[0].type === StructureEditorConfig.optionTypes.radioButton) {
        badges.push(optionTitles[group.value] || group.value);
      } else {
        // eslint-disable-next-line no-use-before-define
        resolveTypes(group.options);
      }
    }
  });
  return badges;
  function resolveTypes(arr) {
    if (badges[0] !== 'Similar') {
      // do not add badges if it's similar mode
      each(arr, (option) => {
        switch (option.type) {
          case StructureEditorConfig.optionTypes.checkbox:
            if (option.value === true || option.subShow) {
              badges.push(option.title);
            }
            break;
          case StructureEditorConfig.optionTypes.input:
            if (!isNaN(parseInt(option.value, 10))) {
              badges.push([option.value, ' ', option.title].join(''));
            }
            break;
          default:
            break;
        }
      });
    }
  }
}

const isChecked = (value) =>
  (value.type === 'checkbox' && isBoolean(value.value) && value.value) || (value.type === 'input' && value.value);

export function getShowMoreState() {
  let showMore = false;
  const more = find(defaultOptions.groups, { id: StructureEditorConfig.optionsIds.MORE.id });
  if (more) {
    each(more.options, (value) => {
      showMore = isChecked(value) || showMore;
    });
  }
  return showMore;
}
export function getCheckedOptions(groups) {
  each(groups, (group) => {
    if (group.options) {
      each(group.options, (option) => {
        // extractOptionValues method see through checkboxes which are checked, initially all checked options are undefined,
        // but default options have property 'predefined', so need to set checked = predefined
        if (option.type === 'checkbox') {
          option.checked = isUndefined(option.checked) ? option.predefined : option.checked;
        }
      });
    }
  });
  return groups;
}
